import { Autocomplete, Paper, Select, TextInput } from '../../../components/common/Form'
import { Api } from '../../../store'
import { InputFormProps, MAX_MAX_BITRATE_MBPS } from '../Edit/InputForm'
import Divider from '@mui/material/Divider'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { AllowUncontrolled } from '../../../components/common/Form/RHF'
import { Overlay } from 'common/api/v1/types'
import { useAsync } from 'react-async-hook'
import Pendable from '../../common/Pendable'
import { FFmpegParamsInputsAudio } from './FFmpegParamsInputsAudio'

export const TranscodingSection = () => {
  const { getValues, setValue, watch } = useFormContext<InputFormProps & AllowUncontrolled>()
  const values = getValues()

  // Watch specific fields that control conditional rendering
  const paramsKey = 'deriveFrom.ingestTransform.ffmpegParams'
  watch(`${paramsKey}.codec`)
  watch(`${paramsKey}.overlay`)
  watch(`${paramsKey}.resolution`)
  watch(`${paramsKey}.pixelFormat`)
  watch(`${paramsKey}.bitrateMode`)

  const ffmpegParams =
    values?.deriveFrom?.ingestTransform?.type === 'transcode'
      ? values?.deriveFrom?.ingestTransform.ffmpegParams
      : undefined
  const isVideoCodec = !!ffmpegParams?.codec
  const isOverlay = !!ffmpegParams?.overlayId
  const isResolution = !!ffmpegParams?.resolution
  const isPixelFormat = !!ffmpegParams?.pixelFormat
  const isDeinterlacing = !!ffmpegParams?.deinterlace
  const isCBR = ffmpegParams?.bitrateMode === 'CBR'

  useEffect(() => {
    // If codec is not set, reset dependent fields
    if (!isVideoCodec) {
      setValue('transcodeTotalBitrateMbps', '')
      setValue('transcodeVideoBitrateMbps', '')
      setValue(`${paramsKey}.resolution`, undefined)
      setValue(`${paramsKey}.overlayId`, undefined)
      setValue(`${paramsKey}.pixelFormat`, undefined)
      setValue(`${paramsKey}.framerate`, undefined)
    }
  }, [isVideoCodec, setValue])

  useEffect(() => {
    if (values?.deriveFrom?.ingestTransform?.type === 'transcode')
      if (values?.deriveFrom?.ingestTransform?.ffmpegParams?.type !== 'transcode') {
        setValue(`deriveFrom.ingestTransform.ffmpegParams.type`, 'transcode')
      }
  }, [values?.deriveFrom?.ingestTransform])

  // Get initial overlay
  const initOverlay = useAsync(async (): Promise<Overlay | undefined> => {
    const transform = values.deriveFrom?.ingestTransform
    if (!transform || transform.type !== 'transcode' || !transform.ffmpegParams.overlayId) {
      return
    }

    return Api.overlaysApi.getOverlay(transform.ffmpegParams.overlayId)
  }, [])

  return (
    <Paper title="Transcoding">
      <Select
        label="Video codec"
        name="deriveFrom.ingestTransform.ffmpegParams.codec"
        options={[
          { name: 'Keep original', value: '' },
          { name: 'H.264 (AVC)', value: 'h264' },
          { name: 'H.265 (HEVC)', value: 'h265' },
        ]}
        tooltip="Change codec using hardware acceleration"
      />
      {isVideoCodec && (
        <>
          <Select
            label={'Bitrate control mode (VBR/CBR)'}
            name="deriveFrom.ingestTransform.ffmpegParams.bitrateMode"
            options={[
              { name: 'VBR - Variable bitrate', value: 'VBR' },
              { name: 'CBR - Constant bitrate', value: 'CBR' },
            ]}
            tooltip="Choose between Variable Bitrate (VBR) or Constant Bitrate (CBR) mode"
          />
          {isCBR && (
            <TextInput
              label="Total Bitrate (Mbps)"
              name="transcodeTotalBitrateMbps"
              required
              noNegative
              validators={{
                number: {
                  lessThanOrEqualTo: MAX_MAX_BITRATE_MBPS,
                  greaterThan: 0,
                  message: `Must be greater than zero and no more than ${MAX_MAX_BITRATE_MBPS}`,
                },
              }}
              type="number"
              tooltip="Set the total constant bitrate for the output transport stream. Must be large enough to accommodate the video and audio streams"
            />
          )}

          <TextInput
            label="Video Bitrate (Mbps)"
            name="transcodeVideoBitrateMbps"
            noNegative
            validators={{
              number: {
                lessThanOrEqualTo: MAX_MAX_BITRATE_MBPS,
                greaterThan: 0,
                message: `Must be greater than zero and no more than ${MAX_MAX_BITRATE_MBPS}`,
              },
            }}
            type="number"
            tooltip="Adjust the compression level by setting the target bitrate for the video encoder"
            newLine
          />

          <Select
            label="Framerate"
            name="deriveFrom.ingestTransform.ffmpegParams.framerate"
            options={[{ name: 'Keep original', value: '' }].concat(
              [
                { name: '25', value: '25' },
                { name: '29.97', value: '30000/1001' },
                { name: '50', value: '50' },
                { name: '59.94', value: '60000/1001' },
              ].map((framerate) => ({ name: framerate.name, value: framerate.value })),
            )}
            tooltip="Convert framerate up or down, dropping extra frames or duplicating intermediate frames"
          />
          <Select
            label={!isResolution ? 'Pixel format' : 'Pixel format (cannot be used with resolution scaling)'}
            name="deriveFrom.ingestTransform.ffmpegParams.pixelFormat"
            options={[
              { name: 'Keep original', value: '' },
              { name: 'YUV 4:2:0 Progressive', value: 'yuv420p' },
              { name: 'YUV 4:2:2 Progressive', value: 'yuv422p', disabled: true },
              { name: 'YUV 4:4:4 Progressive', value: 'yuv444p' },
            ]}
            tooltip="Pixel format choice depends on the hardware accelerator's capability. Cannot be used with resolution scaling"
            disabled={isResolution}
          />
          <Pendable pending={initOverlay.loading}>
            <Autocomplete<Overlay>
              label={
                !(isResolution || isDeinterlacing)
                  ? 'Overlay'
                  : 'Overlay (cannot be used with resolution scaling or deinterlacing)'
              }
              name="deriveFrom.ingestTransform.ffmpegParams.overlayId"
              tooltip="Add a channel logo or other overlays to the transformed input. Cannot be used with resolution scaling"
              disabled={isResolution || isDeinterlacing}
              defaultOption={initOverlay.result}
              api={(params) => {
                return Api.overlaysApi.listOverlays(params)
              }}
              getOptionValue={(overlay) => overlay.id}
              getOptionLabel={(overlay) => overlay.name}
              getOptionKey={(overlay) => overlay.id!}
              optionComparator={(o1, o2) => o1.id === o2.id}
            />
          </Pendable>
          <Select
            label={
              !(isOverlay || isPixelFormat)
                ? 'Resolution scaling'
                : 'Resolution scaling (cannot be used with overlay or pixel format)'
            }
            name="deriveFrom.ingestTransform.ffmpegParams.resolution"
            options={[
              { name: 'Keep original', value: '' },
              { name: '2160p', value: '3840:2160' },
              { name: '1080p', value: '1920:1080' },
              { name: '720p', value: '1280:720' },
            ]}
            tooltip="Scale the resolution of the transformed input (Only available with NVIDIA accelerators). Cannot be used with overlay or pixel format"
            disabled={isOverlay || isPixelFormat}
          />
          <Select
            label={!isOverlay ? 'Deinterlace' : 'Deinterlace (cannot be used with overlay)'}
            name="deriveFrom.ingestTransform.ffmpegParams.deinterlace"
            options={[
              { name: 'Keep original', value: '' },
              { name: 'Deinterlace', value: 'yadif' },
            ]}
            tooltip="Enable or disable YADIF deinterlacing. Only enable on interlaced video inputs. Cannot be used with overlays"
            disabled={isOverlay}
          />
        </>
      )}

      <Divider sx={{ width: '100%', mt: 3 }} />
      <FFmpegParamsInputsAudio />
    </Paper>
  )
}
